import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatChipInputEvent } from "@angular/material/chips";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "app-send-email-users",
  templateUrl: "./send-email-users.component.html",
  styleUrls: ["./send-email-users.component.scss"],
})
export class SendEmailUsersComponent implements OnInit {
  @Input() loading: Boolean;
  @Input() departments: any[];
  @Input() companyData: any;
  @Input() userData: any;
  emails: string[] = [];
  visible = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  selectedDepartment: any;
  selectedPolicy: any;
  accessTypeValid: boolean = true;
  availableAccessTypes: string[] = [];
  notificationOptions: Array<{ value: 'user'|'group'|'company', labelKey: string }> = [];
  private notificationLabelKeyMap = new Map<'user'|'group'|'company', string>();
  private _policies: any[] = [];
  @Input() set policies(value: any[]) {
    this._policies = Array.isArray(value) ? value : [];
    this.selectDefaultPolicyIfNeeded();
  }
  get policies(): any[] {
    return this._policies;
  }
  notificationMode: 'userOnly' | 'groupAdmin' | 'upper' = 'userOnly';

  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  form: UntypedFormGroup;
  @Output() sendInfo = new EventEmitter<any>();

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    const accessPriority = ['user', 'groupadmin', 'company', 'agency', 'hub', 'employee'];
    if (this.userData && this.userData.accessType) {
      const userAccessIndex = accessPriority.indexOf(this.userData.accessType.toLowerCase());
      this.availableAccessTypes = accessPriority.filter((type, index) => index <= userAccessIndex);
    }

    this.form = this.formBuilder.group({
      emails: ["", [Validators.required]],
      group: [""],
      accessType: ["user", [Validators.required]],
      policy: [""],
      notificationsTargets: [["user"] as ('user'|'group'|'company')[]],
    });

    this.form.get('accessType')!.valueChanges.subscribe((value) => {
      this.validateAccessType(value);
      this.configureNotificationsByAccessType(value);
    });

    this.validateAccessType(this.form.get('accessType')!.value);
    this.configureNotificationsByAccessType(this.form.get('accessType')!.value);
    this.selectDefaultPolicyIfNeeded();
  }

  private configureNotificationsByAccessType(accessType: string) {
    const at = String(accessType || '').toLowerCase();
    const ctrl = this.form.get('notificationsTargets') as UntypedFormControl;

    if (at === 'user')            this.notificationMode = 'userOnly';
    else if (at === 'groupadmin') this.notificationMode = 'groupAdmin';
    else                          this.notificationMode = 'upper';

    if (this.notificationMode === 'userOnly') {
      this.notificationOptions = [
        { value: 'user',  labelKey: 'user.notifications.receive-user' },
      ];
    } else if (this.notificationMode === 'groupAdmin') {
      this.notificationOptions = [
        { value: 'user',  labelKey: 'user.notifications.receive-user' },
        { value: 'group', labelKey: 'user.notifications.group' },
      ];
    } else {
      this.notificationOptions = [
        { value: 'user',    labelKey: 'user.notifications.receive-user' },
        { value: 'company', labelKey: 'user.notifications.company' },
      ];
    }
    this.notificationLabelKeyMap = new Map(this.notificationOptions.map(o => [o.value, o.labelKey]));


    const allowed = new Set(this.notificationOptions.map(o => o.value));
    let curr: ('user'|'group'|'company')[] =
      ((ctrl.value || []) as any[]).filter(v => allowed.has(v)) as any;

    if (curr.length > 1) curr = [curr[0]];
    ctrl.setValue(curr, { emitEvent: false });
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  validateAccessType(selectedType: string): void {
    if (selectedType === 'agency' && !this.companyData?.agencyUuid) {
      this.accessTypeValid = false;
    } else if (selectedType === 'hub' && !this.companyData?.hubUuid) {
      this.accessTypeValid = false;
    } else {
      this.accessTypeValid = true;
    }
  }

  private selectDefaultPolicyIfNeeded(): void {
    if (!this.form) return;
    const ctrl = this.form.get('policy');
    if (!ctrl || ctrl.value || !this.policies?.length) return;

    const def = this.policies.find(p => (p?.name ?? '').trim().toLowerCase() === 'default');
    const toId = (p: any) => p?.uuid || p?._id;
    const selectedId = toId(def) || toId(this.policies[0]);
    if (selectedId) ctrl.patchValue(selectedId);
  }

  add(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;
    if ((value || "").trim()) this.emails.push(value.trim());
    if (input) input.value = "";
  }

  remove(email): void {
    const index = this.emails.indexOf(email);
    if (index >= 0) this.emails.splice(index, 1);
  }

  isMode(mode: 'userOnly' | 'groupAdmin' | 'upper') {
    return this.notificationMode === mode;
  }

  onSubmit() {
    const selected: ('user'|'group'|'company')[] = this.form.get('notificationsTargets')!.value || [];
    const notifications = { company: false, group: false, user: false };
    if (selected.includes('user'))    notifications.user = true;
    if (selected.includes('group'))   notifications.group = true;
    if (selected.includes('company')) notifications.company = true;

    const data = {
      emails: this.emails,
      group: this.selectedDepartment,
      accessType: this.form.get('accessType')!.value,
      policy: this.form.get('policy')?.value || null,
      notifications
    };

    this.sendInfo.emit(data);
  }

  onGroupChange(group) {
    this.selectedDepartment = group;
  }

  onNotifOptionToggle(ev: any) {
    if (!ev.isUserInput) return;
    const ctrl = this.form.get('notificationsTargets') as UntypedFormControl;
    const val: ('user'|'group'|'company') = ev.source.value;

    if (ev.source.selected) ctrl.setValue([val]);
    else ctrl.setValue([]);
  }


  get notifSelectedValues(): ('user'|'group'|'company')[] {
    return (this.form?.get('notificationsTargets')?.value as any[]) ?? [];
  }

  get notificationsTrigger(): string {
    const vals = this.notifSelectedValues;
    if (!vals.length) return this.translate.instant('user.notifications.none');
    return vals
      .map(v => this.translate.instant(this.notificationLabelKeyMap.get(v) ?? ''))
      .filter(Boolean)
      .join(', ');
  }

  closeModal() {
    this.activeModal.dismiss("Cross click");
  }
}